import { useState, useEffect } from "react";
import { useLocaleNavigate } from "@/hooks/useLocaleNavigate";
import { LocaleLink } from "@/components/LocaleLink";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { z } from "zod";
import { ArrowLeft } from "lucide-react";
import logo from "@/assets/cf-vertical-2.svg";
import AnimatedBackground from "@/components/auth/AnimatedBackground";
import SEOHead from "@/components/seo/SEOHead";
import { useTranslation } from "react-i18next";

type Mode = "login" | "register";

const Auth = () => {
  const { t } = useTranslation();
  const navigate = useLocaleNavigate();
  const { user, signIn, signUp, isLoading } = useAuth();
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

  const emailSchema = z.string().email(t("auth.invalidEmail"));
  const passwordSchema = z.string().min(6, t("auth.passwordMin"));

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  const validateForm = () => {
    const newErrors: { email?: string; password?: string } = {};
    const emailResult = emailSchema.safeParse(email);
    if (!emailResult.success) newErrors.email = emailResult.error.errors[0].message;
    const passwordResult = passwordSchema.safeParse(password);
    if (!passwordResult.success) newErrors.password = passwordResult.error.errors[0].message;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);

    try {
      if (mode === "login") {
        const { error } = await signIn(email, password);
        if (error) {
          toast.error(error.message.includes("Invalid login credentials") ? t("auth.invalidCredentials") : error.message);
          return;
        }
        toast.success(t("auth.loginSuccess"));
      } else {
        const { error } = await signUp(email, password);
        if (error) {
          toast.error(error.message.includes("already registered") ? t("auth.alreadyRegistered") : error.message);
          return;
        }
        toast.success(t("auth.registrationSuccess"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-caption">{t("common.loading")}</p>
      </div>
    );
  }

  return (
    <main id="main-content" className="min-h-screen bg-background flex flex-col items-center justify-center section-padding relative overflow-hidden">
      <SEOHead title={t("auth.seoTitle")} description={t("auth.seoDesc")} canonical="/auth" noindex />
      <AnimatedBackground />
      <div className="w-full flex justify-center mb-12 relative z-10">
        <img src={logo} alt="CRAFTFINDUS" className="h-40 sm:h-52" />
      </div>
      <div className="w-full max-w-md space-y-8 relative z-10">
        <div className="text-center space-y-3">
          <LocaleLink to="/" className="inline-flex items-center gap-1.5 text-caption link-subtle">
            <ArrowLeft className="w-3.5 h-3.5" />
            {t("auth.backToHome")}
          </LocaleLink>
          <p className="text-caption">{t("auth.loginPrompt")}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" noValidate>
          <div className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="auth-email" className="text-micro block">{t("auth.emailLabel")}</label>
              <input
                id="auth-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setErrors(prev => ({ ...prev, email: undefined })); }}
                className="w-full h-12 px-4 border border-border bg-background text-body focus:border-primary transition-colors"
                placeholder={t("auth.emailPlaceholder")}
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? "auth-email-error" : undefined}
              />
              {errors.email && <p id="auth-email-error" className="text-xs text-destructive" role="alert">{errors.email}</p>}
            </div>

            <div className="space-y-2">
              <label htmlFor="auth-password" className="text-micro block">{t("auth.passwordLabel")}</label>
              <input
                id="auth-password"
                type="password"
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setErrors(prev => ({ ...prev, password: undefined })); }}
                className="w-full h-12 px-4 border border-border bg-background text-body focus:border-primary transition-colors"
                placeholder={t("auth.passwordPlaceholder")}
                aria-invalid={!!errors.password}
                aria-describedby={errors.password ? "auth-password-error" : undefined}
              />
              {errors.password && <p id="auth-password-error" className="text-xs text-destructive" role="alert">{errors.password}</p>}
            </div>
          </div>

          <Button type="submit" variant="commit" size="lg" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? t("common.pleaseWait") : t("auth.loginBtn")}
          </Button>
        </form>
      </div>
    </main>
  );
};

export default Auth;
